import React, { useState } from 'react';
import { 
  CheckCircle2,
  AlertCircle,
  XCircle,
  FileText,
  Shield,
  Activity,
  TrendingUp,
  Download,
  Calendar,
  Clock,
  Award,
  AlertTriangle,
} from 'lucide-react';
import { Card } from '../../ui/Card'
import { Badge } from '../../ui/Badge'
import { Button } from '../../ui/Button'
import { cn } from '../../../utils/cn'
import { formatRelativeTime } from '../../../utils/format'

interface ComplianceStandard {
  id: string;
  name: string;
  shortName: string;
  status: 'compliant' | 'partial' | 'non-compliant' | 'pending';
  score: number;
  lastAudit: Date;
  nextAudit: Date;
  controls: {
    total: number;
    passed: number;
    failed: number;
    notApplicable: number;
  };
}

interface ComplianceControl {
  id: string;
  name: string;
  category: string;
  status: 'pass' | 'fail' | 'warning' | 'not-applicable';
  severity: 'critical' | 'high' | 'medium' | 'low';
  lastChecked: Date;
  description: string;
}

// Mock data
const mockStandards: ComplianceStandard[] = [
  {
    id: 'soc2',
    name: 'SOC 2 Type 1',
    shortName: 'SOC 2',
    status: 'compliant',
    score: 94,
    lastAudit: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000),
    nextAudit: new Date(Date.now() + 60 * 24 * 60 * 60 * 1000),
    controls: { total: 120, passed: 113, failed: 3, notApplicable: 4 },
  },
  {
    id: 'iso27001',
    name: 'ISO 27001:2022',
    shortName: 'ISO 27001',
    status: 'partial',
    score: 78,
    lastAudit: new Date(Date.now() - 45 * 24 * 60 * 60 * 1000),
    nextAudit: new Date(Date.now() + 45 * 24 * 60 * 60 * 1000),
    controls: { total: 93, passed: 72, failed: 15, notApplicable: 6 },
  },
  {
    id: 'gdpr',
    name: 'GDPR Compliance',
    shortName: 'GDPR',
    status: 'compliant',
    score: 98,
    lastAudit: new Date(Date.now() - 15 * 24 * 60 * 60 * 1000),
    nextAudit: new Date(Date.now() + 75 * 24 * 60 * 60 * 1000),
    controls: { total: 50, passed: 49, failed: 0, notApplicable: 1 },
  },
  {
    id: 'hipaa',
    name: 'HIPAA Security Rule',
    shortName: 'HIPAA',
    status: 'pending',
    score: 0,
    lastAudit: new Date(0),
    nextAudit: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000),
    controls: { total: 75, passed: 0, failed: 0, notApplicable: 0 },
  },
];

const mockControls: ComplianceControl[] = [
  {
    id: 'ctrl-1',
    name: 'Data Encryption at Rest',
    category: 'Security',
    status: 'pass',
    severity: 'critical',
    lastChecked: new Date(Date.now() - 2 * 60 * 60 * 1000),
    description: 'All data is encrypted using AES-256',
  },
  {
    id: 'ctrl-2',
    name: 'Access Control Logging',
    category: 'Monitoring',
    status: 'warning',
    severity: 'high',
    lastChecked: new Date(Date.now() - 4 * 60 * 60 * 1000),
    description: 'Some access logs missing retention policy',
  },
  {
    id: 'ctrl-3',
    name: 'Regular Security Training',
    category: 'Personnel',
    status: 'fail',
    severity: 'medium',
    lastChecked: new Date(Date.now() - 24 * 60 * 60 * 1000),
    description: 'Annual training overdue for 15% of staff',
  },
];

const getStatusColor = (status: string) => {
  switch (status) {
    case 'compliant':
    case 'pass': return 'success';
    case 'partial':
    case 'warning': return 'warning';
    case 'non-compliant':
    case 'fail': return 'danger';
    case 'pending': return 'secondary';
    default: return 'secondary';
  }
};

const getStatusIcon = (status: string) => {
  switch (status) {
    case 'compliant':
    case 'pass': return CheckCircle2;
    case 'partial':
    case 'warning': return AlertCircle;
    case 'non-compliant':
    case 'fail': return XCircle;
    case 'pending': return Clock;
    default: return AlertCircle;
  }
};

export const MobileComplianceMonitor: React.FC = () => {
  const [selectedStandard, setSelectedStandard] = useState<string | null>(null);
  const [showControls, setShowControls] = useState(false);

  const overallCompliance = {
    score: Math.round(
      mockStandards
        .filter(s => s.status !== 'pending')
        .reduce((sum, s) => sum + s.score, 0) / 
      mockStandards.filter(s => s.status !== 'pending').length
    ),
    compliant: mockStandards.filter(s => s.status === 'compliant').length,
    partial: mockStandards.filter(s => s.status === 'partial').length,
    nonCompliant: mockStandards.filter(s => s.status === 'non-compliant').length,
    pending: mockStandards.filter(s => s.status === 'pending').length,
  };

  return (
    <div className="space-y-4">
      {/* Overall Compliance Score */}
      <Card className="bg-gradient-to-r from-primary-600/10 to-accent-600/10 border-primary-600/30">
        <div className="p-4">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h3 className="text-lg font-semibold text-console-text">Overall Compliance</h3>
              <p className="text-xs text-console-muted">Across all standards</p>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-console-text">{overallCompliance.score}%</div>
              <Badge variant="success" className="text-xs">Compliant</Badge>
            </div>
          </div>
          
          <div className="grid grid-cols-4 gap-2 text-center">
            <div>
              <div className="text-lg font-semibold text-success-500">{overallCompliance.compliant}</div>
              <div className="text-xs text-console-muted">Compliant</div>
            </div>
            <div>
              <div className="text-lg font-semibold text-warning-500">{overallCompliance.partial}</div>
              <div className="text-xs text-console-muted">Partial</div>
            </div>
            <div>
              <div className="text-lg font-semibold text-danger-500">{overallCompliance.nonCompliant}</div>
              <div className="text-xs text-console-muted">Failed</div>
            </div>
            <div>
              <div className="text-lg font-semibold text-gray-500">{overallCompliance.pending}</div>
              <div className="text-xs text-console-muted">Pending</div>
            </div>
          </div>
        </div>
      </Card>

      {/* Quick Actions */}
      <div className="grid grid-cols-2 gap-2">
        <Button 
          variant={!showControls ? "primary" : "secondary"} 
          size="sm"
          onClick={() => setShowControls(false)}
        >
          Standards
        </Button>
        <Button 
          variant={showControls ? "primary" : "secondary"} 
          size="sm"
          onClick={() => setShowControls(true)}
        >
          Controls
        </Button>
      </div>

      {/* Standards View */}
      {!showControls && (
        <div className="space-y-3">
          <h3 className="text-sm font-medium text-console-text px-1">Compliance Standards</h3>
          
          {mockStandards.map((standard) => {
            const StatusIcon = getStatusIcon(standard.status);
            
            return (
              <Card 
                key={standard.id} 
                className={cn(
                  "bg-gray-900 cursor-pointer transition-all",
                  selectedStandard === standard.id && "ring-2 ring-primary-600"
                )}
                onClick={() => setSelectedStandard(standard.id === selectedStandard ? null : standard.id)}
              >
                <div className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <StatusIcon className={cn(
                        "w-5 h-5",
                        `text-${getStatusColor(standard.status)}-500`
                      )} />
                      <div>
                        <h4 className="font-medium text-console-text">{standard.shortName}</h4>
                        <p className="text-xs text-console-muted">{standard.name}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-xl font-bold text-console-text">
                        {standard.status === 'pending' ? '--' : `${standard.score}%`}
                      </div>
                      <Badge variant={getStatusColor(standard.status)} className="text-xs">
                        {standard.status}
                      </Badge>
                    </div>
                  </div>

                  {standard.status !== 'pending' && (
                    <div className="grid grid-cols-3 gap-3 mb-3">
                      <div>
                        <div className="text-xs text-console-muted">Passed</div>
                        <div className="text-sm font-medium text-success-500">
                          {standard.controls.passed}
                        </div>
                      </div>
                      <div>
                        <div className="text-xs text-console-muted">Failed</div>
                        <div className="text-sm font-medium text-danger-500">
                          {standard.controls.failed}
                        </div>
                      </div>
                      <div>
                        <div className="text-xs text-console-muted">N/A</div>
                        <div className="text-sm font-medium text-gray-500">
                          {standard.controls.notApplicable}
                        </div>
                      </div>
                    </div>
                  )}

                  {selectedStandard === standard.id && (
                    <div className="border-t border-gray-800 pt-3 space-y-2">
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-console-muted">Last Audit</span>
                        <span className="text-console-text">
                          {standard.lastAudit.getTime() > 0 
                            ? formatRelativeTime(standard.lastAudit)
                            : 'Never'}
                        </span>
                      </div>
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-console-muted">Next Audit</span>
                        <span className="text-console-text">
                          {formatRelativeTime(standard.nextAudit)}
                        </span>
                      </div>
                      <div className="flex gap-2 mt-3">
                        <Button variant="secondary" size="sm" className="flex-1">
                          <FileText className="w-3 h-3 mr-1" />
                          View Report
                        </Button>
                        <Button variant="primary" size="sm" className="flex-1">
                          <Download className="w-3 h-3 mr-1" />
                          Export
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* Controls View */}
      {showControls && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-medium text-console-text px-1">Recent Control Checks</h3>
            <Badge variant="secondary" className="text-xs">
              {mockControls.length} Active
            </Badge>
          </div>
          
          {mockControls.map((control) => {
            const StatusIcon = getStatusIcon(control.status);
            
            return (
              <Card key={control.id} className="bg-gray-900">
                <div className="p-4">
                  <div className="flex items-start gap-3">
                    <StatusIcon className={cn(
                      "w-5 h-5 mt-0.5",
                      `text-${getStatusColor(control.status)}-500`
                    )} />
                    <div className="flex-1">
                      <div className="flex items-start justify-between mb-1">
                        <h4 className="font-medium text-console-text text-sm">{control.name}</h4>
                        <Badge 
                          variant={
                            control.severity === 'critical' ? 'danger' :
                            control.severity === 'high' ? 'warning' :
                            'secondary'
                          } 
                          className="text-xs ml-2"
                        >
                          {control.severity}
                        </Badge>
                      </div>
                      <p className="text-xs text-console-muted mb-2">{control.description}</p>
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-console-muted">{control.category}</span>
                        <span className="text-console-muted">
                          Checked {formatRelativeTime(control.lastChecked)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* Compliance Actions */}
      <Card className="bg-gray-900">
        <div className="p-4">
          <h3 className="text-sm font-medium text-console-text mb-3">Compliance Actions</h3>
          <div className="grid grid-cols-2 gap-2">
            <Button variant="primary" size="sm" className="flex items-center justify-center gap-2">
              <Activity className="w-4 h-4" />
              Run Audit
            </Button>
            <Button variant="secondary" size="sm" className="flex items-center justify-center gap-2">
              <Download className="w-4 h-4" />
              Export All
            </Button>
            <Button variant="secondary" size="sm" className="flex items-center justify-center gap-2">
              <Calendar className="w-4 h-4" />
              Schedule
            </Button>
            <Button variant="secondary" size="sm" className="flex items-center justify-center gap-2">
              <Award className="w-4 h-4" />
              Certificates
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
};